import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SEO } from '@/components/common/SEO';
import BottomNav from '@/components/layouts/Footer/BottomNav';
import MarketingBanners from '@/components/home/MarketingBanners';
import VendorsByCategory from '@/components/home/VendorsByCategory';
import Loader from '@/components/common/Loader';
import { PwaPrompt } from '@/components/modals/PwaPrompt';
import { useAsync } from '@/hooks/useAsync';
import { useUser } from '../contexts/UserContext';
import { useVendor } from '../contexts/VendorContext';
import axiosInstance from '../utils/axiosInstance';
import SearchHeader from '../components/home/SearchHeader';
import CategorySelector from '../components/home/CategorySelector';

const HomePage = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useUser();
  const { vendors, setVendors } = useVendor();
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [showPrompt, setShowPrompt] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const { loading: vendorsLoading, execute: executeVendorsFetch } = useAsync();
  const [vendorCategories, setVendorCategories] = useState([]);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Get auth tokens
  const sessionToken = localStorage.getItem('sessionToken');
  const refreshToken = localStorage.getItem('refreshToken');

  const headers = {
    sessionToken,
    refreshToken,
  };

  // Fetch nearby vendors
  const fetchNearbyVendors = async () => {
    if (!user?.addressModel?.[0]) return;

    const address = user.addressModel[0];

    try {
      const vendorsResponse = await executeVendorsFetch(
        axiosInstance.get(
          `${import.meta.env.VITE_API_URL}/rest/subziwale/api/v1/user/near-by-vendors?plotNo=${
            address?.plotNo
          }&sector=${address?.sector}&state=${address?.state}`,
          { headers }
        )
      );

      setVendors(vendorsResponse?.data);

      // Extract unique categories from all vendors
      const allCategories = vendorsResponse?.data.flatMap(
        (vendor) => vendor.vendorCategories || []
      );

      // Remove duplicates and sort
      const uniqueCategories = [...new Set(allCategories)].sort();

      // Add "All" as the first category
      setVendorCategories(['All', ...uniqueCategories]);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  // Handle category selection
  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
  };

  useEffect(() => {
    if (!user) {
      const contactNo = localStorage.getItem('contactNo');
      refreshUser(contactNo);
    } else if (user?.userType === 'user') {
      fetchNearbyVendors();
    }
  }, [user]);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      setInstallPrompt(e);
      if (!localStorage.getItem('pwaPromptDismissed')) {
        setShowPrompt(true);
      }
    };

    window.scrollTo(0, 0);
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, []);

  useEffect(() => {
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);

    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);

    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  return (
    <div className="bg-white">
      {!isOnline && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-red-500 text-white flex items-center justify-between px-4 py-2 text-sm font-semibold shadow-md animate-slideDown">
          <div className="flex items-center gap-2">
            <svg
              className="h-5 w-5 text-white"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v2m0 4h.01M21 12A9 9 0 1 1 3 12a9 9 0 0 1 18 0z"
              />
            </svg>
            <span>You are offline. Some features may not work properly.</span>
          </div>
          <button
            onClick={() => window.location.reload()}
            className="bg-white text-red-500 px-2 py-1 rounded-md text-xs font-bold hover:bg-red-100 transition"
          >
            Retry
          </button>
        </div>
      )}
      <PwaPrompt
        installPrompt={installPrompt}
        showPrompt={showPrompt}
        setShowPrompt={setShowPrompt}
        setInstallPrompt={setInstallPrompt}
      />
      <SEO
        title="BigLocal - Let's make Local Big"
        description="Order fresh vegetables, fruits, groceries, and more from local vendors. High-quality produce delivered to your doorstep."
        canonicalUrl="https://www.biglocal.in/"
        ogImage="/images/biglocallogo.png"
      />

      <div className="max-w-md mx-auto">
        <SearchHeader />

        <div className="px-4 border-l border-r  pb-12">
          {/* <MarketingBanners /> */}

          {vendorsLoading ? (
            <div className="h-60 flex items-center justify-center">
              <Loader />
            </div>
          ) : (
            <>
              <div className="mt-1 mb-6">
                <CategorySelector
                  onSelectCategory={handleCategorySelect}
                  selectedCategory={selectedCategory}
                  categories={vendorCategories}
                />

                <div className="mt-0">
                  <h3 className="text-lg font-semibold mb-3">
                    {selectedCategory === 'All'
                      ? 'Your Nearby Vendors'
                      : `Vendors for ${selectedCategory}`}
                  </h3>
                  <VendorsByCategory vendors={vendors} category={selectedCategory} />
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <BottomNav page="Home" />
    </div>
  );
};

export default HomePage;
